import type { Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { FollowUpMoment, PatientPresence } from '@common/customControls/bariatrics';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { buikomvangSchema, gewichtInKgSchema, lengteInCmSchema } from '@common/validation/schemas/numberSchemas';
import { addAllowNull } from '@common/validation/utils/addAllowNull';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';

export type BariPostSecretarialAssistantFollowUpData = {
    aanwezigheid: PatientPresence | null;
    assets: ProcessActivityAsset[];
    bijzonderheden_labuitslag: string | null;
    buikomvang: number | null;
    gewicht_in_kg: number | null;
    lengte_in_cm: number | null;
    moment: FollowUpMoment | null;
    toelichting_no_show: string | null;
};

export type BariPostSecretarialAssistantFollowUpReadData = BariPostSecretarialAssistantFollowUpData & {
    datum_operatie: string;
    gewicht_lengte_bmi: string[];
};

export type BariPostSecretarialAssistantFollowUpReadKey = keyof BariPostSecretarialAssistantFollowUpReadData;

export type BariPostSecretarialAssistantFollowUpReadLabel =
    | 'Aanwezigheid'
    | 'Bijzonderheden labuitslag'
    | 'Buikomvang'
    | 'Datum operatie'
    | 'Follow-up gesprek'
    | 'Gewicht / Lengte / BMI'
    | 'Labuitslag'
    | 'Toelichting no-show';

export type BariPostSecretarialAssistantFollowUpReadSectionTitle =
    | 'Aanwezigheid'
    | 'Biometrische gegevens'
    | 'Labwaarden';

export const bariPostSecretarialAssistantFollowUpReadSchema: TypedReadSchema<
    BariPostSecretarialAssistantFollowUpReadKey,
    BariPostSecretarialAssistantFollowUpReadLabel,
    BariPostSecretarialAssistantFollowUpReadSectionTitle
>[] = [
    {
        items: [
            { key: 'datum_operatie', label: 'Datum operatie', type: 'dateAsString' },
            { key: 'moment', label: 'Follow-up gesprek' },
            { key: 'aanwezigheid', label: 'Aanwezigheid' },
            { key: 'toelichting_no_show', label: 'Toelichting no-show' },
        ],
        sectionTitle: 'Aanwezigheid',
    },
    {
        items: [
            { key: 'assets', label: 'Labuitslag' },
            { key: 'bijzonderheden_labuitslag', label: 'Bijzonderheden labuitslag' },
        ],
        sectionTitle: 'Labwaarden',
    },
    {
        items: [
            { key: 'gewicht_lengte_bmi', label: 'Gewicht / Lengte / BMI' },
            { append: 'cm', key: 'buikomvang', label: 'Buikomvang', type: 'number' },
        ],
        sectionTitle: 'Biometrische gegevens',
    },
];

function whenPatientWasPresent(then: Joi.SchemaLike, otherwise = Koi.valid(null)) {
    return Koi.when('aanwezigheid', {
        is: PatientPresence.present,
        otherwise,
        then,
    });
}

const bariPostSecretarialAssistantFollowUpFinal: TypedSchema<BariPostSecretarialAssistantFollowUpData> = {
    aanwezigheid: Koi.koi().enum(PatientPresence),
    assets: whenPatientWasPresent(getProcessActivityAssetsSchema({ min: 1 }), getProcessActivityAssetsSchema()),
    bijzonderheden_labuitslag: whenPatientWasPresent(getTextAreaStringSchema(true)),
    buikomvang: whenPatientWasPresent(buikomvangSchema),
    gewicht_in_kg: whenPatientWasPresent(gewichtInKgSchema),
    lengte_in_cm: whenPatientWasPresent(lengteInCmSchema),
    moment: Koi.koi().enum(FollowUpMoment),
    toelichting_no_show: Koi.when('aanwezigheid', {
        is: PatientPresence.notPresent,
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(),
    }),
};

const bariPostSecretarialAssistantFollowUpDraft: TypedSchema<BariPostSecretarialAssistantFollowUpData> = addAllowNull(
    {
        ...bariPostSecretarialAssistantFollowUpFinal,
        assets: getProcessActivityAssetsSchema(),
    },
    ['assets']
);

export const bariPostSecretarialAssistantFollowUpDraftSchema = Koi.object(bariPostSecretarialAssistantFollowUpDraft);
export const bariPostSecretarialAssistantFollowUpFinalSchema = Koi.object(bariPostSecretarialAssistantFollowUpFinal);
